/**
 * Query Keys for TanStack Query
 * 
 * Organized hierarchically for easy cache invalidation:
 * - queryKeys.colonies.list() -> ['colonies']
 * - queryKeys.colonies.detail(id) -> ['colonies', id]
 * - queryKeys.brains.list(colonyId) -> ['brains', colonyId]
 */

export const queryKeys = {
  // User
  user: {
    all: () => ['user'] as const,
    profile: () => ['user', 'profile'] as const,
  },

  // Colonies
  colonies: {
    all: () => ['colonies'] as const,
    list: () => ['colonies'] as const,
    detail: (id: string) => ['colonies', id] as const,
  },

  // Brains
  brains: {
    all: () => ['brains'] as const,
    list: (colonyId: string) => ['brains', colonyId] as const,
    detail: (id: string) => ['brains', 'detail', id] as const,
  },

  // Threads (Alt-me conversations)
  threads: {
    all: () => ['threads'] as const,
    altme: (colonyId: string) => ['threads', 'altme', colonyId] as const,
    detail: (id: string) => ['threads', id] as const,
  },

  // Messages
  messages: {
    all: () => ['messages'] as const,
    list: (threadId: string, cursor?: string) => 
      cursor ? ['messages', threadId, cursor] as const : ['messages', threadId] as const,
  },

  // Knowledge
  knowledge: {
    all: () => ['knowledge'] as const,
    list: (colonyId: string) => ['knowledge', colonyId] as const,
    context: (colonyId: string, role: string) => ['knowledge', 'context', colonyId, role] as const,
  },

  // Jobs (indexing status)
  jobs: {
    all: () => ['jobs'] as const,
    detail: (id: string) => ['jobs', id] as const,
  },

  // Traces
  traces: {
    all: () => ['traces'] as const,
    list: () => ['traces'] as const,
  },

  // Legacy keys for backwards compatibility
  conv: (colonyId: string) => ['conv', colonyId] as const,
  msgs: (conversationId: string) => ['msgs', conversationId] as const,
  files: (colonyId: string) => ['files', colonyId] as const,
  colonyStatus: (colonyId?: string) => ['colony-status', colonyId] as const,
  goals: ['goals'] as const,
  futureMeContext: ['future-me-context'] as const,
};

// Legacy export for backwards compatibility
export const qk = queryKeys;